import React, { useState, useRef, useEffect } from "react";
import { 
  Box, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText, 
  IconButton, 
  Typography,
  useTheme,
  Fade,
  Paper
} from "@mui/material";
import { ExpandIcon } from "../../icons/icons-svg/expand-icon";
import german_flag from "../../../img/flag_german.svg";
import british_flag from "../../../img/flag_british.svg";
import PropTypes from "prop-types";


export const ChooseLanguageDropdown = ({ color }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [chosenLanguage, setChosenLanguage] = useState(
    localStorage.getItem("language") || navigator.language.split(/[-_]/)[0]
  );
  const theme = useTheme();
  const open = Boolean(anchorEl);

  const languages = [
    { language: "de", src: german_flag, name: "Deutsch" },
    { language: "en", src: british_flag, name: "English" },
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (language) => {
    setChosenLanguage(language);
    localStorage.setItem("language", language);
    handleClose();
    window.location.reload();
  };

  const currentLanguage = languages.find(lang => lang.language === chosenLanguage) || languages[0];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton
        onClick={handleClick}
        sx={{
          padding: '8px 12px',
          borderRadius: '10px',
          backgroundColor: 'rgba(232, 121, 117, 0.1)',
          border: '1px solid rgba(232, 121, 117, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(232, 121, 117, 0.15)',
            transform: 'scale(1.02)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={currentLanguage.src}
            alt={currentLanguage.name}
            sx={{
              width: '24px',
              height: '16px',
              borderRadius: '2px',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: color || theme.palette.grey[700],
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {chosenLanguage}
          </Typography>
          <ExpandIcon 
            sx={{ 
              width: '16px', 
              height: '16px',
              color: color || theme.palette.grey[600],
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }} 
          />
        </Box>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 121, 117, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            minWidth: '140px',
            '& .MuiMenuItem-root': {
              padding: '8px 16px',
              borderRadius: '8px',
              margin: '4px 8px',
              '&:hover': {
                backgroundColor: 'rgba(232, 121, 117, 0.1)',
              },
            },
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        {languages
          .filter((language) => language.language !== chosenLanguage)
          .map((language) => (
            <MenuItem
              key={language.language}
              onClick={() => handleLanguageChange(language.language)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                component="img"
                src={language.src}
                alt={language.name}
                sx={{
                  width: '24px',
                  height: '16px',
                  borderRadius: '2px',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.palette.grey[700],
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {language.language}
              </Typography>
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

ChooseLanguageDropdown.propTypes = {
  color: PropTypes.string,
};
